import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { ArchVizPage } from "./routes/archviz";
import { DashboardPage } from "./routes/dashboard/index";
import { ItemsPage } from "./routes/dashboard/items";
import { ItemDetailPage } from "./routes/dashboard/items/detail";
import { SettingsPromptsPage } from "./routes/dashboard/settings/prompts";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { DesignProbePage } from "./routes/design-probe";
import { LoginPage } from "./routes/login";
import { SharedSolutionPage } from "./routes/SharedSolutionPage";
import { SignupPage } from "./routes/signup";
import { SolutionPage } from "./routes/solutions/SolutionPage";
import { SolutionsDashboardPage } from "./routes/solutions/SolutionsDashboardPage";
import { SpecificVersionPage } from "./routes/solutions/SpecificVersionPage";
import { VersionHistoryPage } from "./routes/solutions/VersionHistoryPage";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: requireAuth,
  component: () => (
    <DashboardLayout>
      <SolutionsDashboardPage />
    </DashboardLayout>
  ),
});

const archvizRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archviz",
  beforeLoad: requireAuth,
  component: ArchVizPage,
});

const solutionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/solutions/$slug",
  beforeLoad: requireAuth,
  component: () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const params = solutionRoute.useParams();
    return <SolutionPage slug={params.slug} />;
  },
});

const versionHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/solutions/$slug/versions",
  beforeLoad: requireAuth,
  component: () => {
    const params = versionHistoryRoute.useParams();
    return <VersionHistoryPage slug={params.slug} />;
  },
});

const specificVersionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/solutions/$slug/v/$versionId",
  beforeLoad: requireAuth,
  component: () => {
    const params = specificVersionRoute.useParams();
    return <SpecificVersionPage slug={params.slug} versionId={params.versionId} />;
  },
});

const sharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/$token",
  component: () => {
    const params = sharedRoute.useParams();
    return <SharedSolutionPage token={params.token} />;
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

// THROWAWAY — delete with the Commit 2 alias swap
const designProbeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/design-probe",
  component: DesignProbePage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const itemsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items",
  component: ItemsPage,
});

const itemDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items/$itemId",
  component: ItemDetailPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsPromptsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/prompts",
  component: SettingsPromptsPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  archvizRoute,
  solutionRoute,
  versionHistoryRoute,
  specificVersionRoute,
  sharedRoute,
  loginRoute,
  signupRoute,
  designProbeRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    itemsRoute,
    itemDetailRoute,
    settingsLayout.addChildren([settingsUsersRoute, settingsPromptsRoute, settingsSystemRoute]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
